/**
 * Error Boundary Component
 * Catches React component errors and reports them to audit system
 */

import { Component, ErrorInfo, ReactNode } from 'react';
import i18n from '../i18n';
import { reportError } from '../services/errorReporting';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error,
      errorInfo: null
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log error details to console
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    // Update state with error info
    this.setState({
      errorInfo
    });

    // Report to audit system
    reportError(error, {
      component: 'ErrorBoundary',
      additionalContext: {
        componentStack: errorInfo.componentStack,
        errorBoundary: true
      }
    });
  }

  handleReset = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });
    // Reload the page to reset state
    window.location.reload();
  };

  render(): ReactNode {
    if (this.state.hasError) {
      // Fallback UI
      return (
        <div className="min-h-screen w-full flex items-center justify-center bg-gradient-to-br from-red-50 via-orange-50 to-pink-50">
          <div className="glass-pane px-12 py-16 max-w-2xl mx-4 text-center">
            <div className="mb-6">
              <svg
                className="w-24 h-24 text-red-500 mx-auto mb-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
              <h1 className="text-3xl font-bold text-gray-800 mb-2">
                {i18n.t('common:errors.somethingWentWrong')}
              </h1>
              <p className="text-gray-600 mb-6">
                {i18n.t('common:errors.unexpectedError')}
              </p>
            </div>

            {this.state.error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 text-left">
                <p className="text-sm font-semibold text-red-800 mb-2">
                  {i18n.t('common:errors.errorDetails')}
                </p>
                <p className="text-sm text-red-700 font-mono break-all">
                  {this.state.error.message}
                </p>
              </div>
            )}

            <div className="space-y-3">
              <button
                onClick={this.handleReset}
                className="btn-primary w-full"
              >
                {i18n.t('common:errors.reloadApplication')}
              </button>
              <button
                onClick={() => window.history.back()}
                className="btn-secondary w-full"
              >
                {i18n.t('common:errors.goBack')}
              </button>
            </div>

            {import.meta.env.DEV && this.state.errorInfo && (
              <details className="mt-6 text-left bg-gray-50 rounded-lg p-4">
                <summary className="cursor-pointer text-sm font-semibold text-gray-700 mb-2">
                  {i18n.t('common:errors.componentStack')}
                </summary>
                <pre className="text-xs text-gray-600 overflow-auto whitespace-pre-wrap">
                  {this.state.errorInfo.componentStack}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
